'use client';

import { useActiveHighlightSection } from '@/app/(consent-manger)/[locale]/(lagacy-footer)/document/[...segments]/hooks/useActiveHighlightSection';
import { ElementOptionsMenu } from '@/components/ui/element-options-menu';
import styles from '@/components/ui/html-link-handler.module.css';
import LegislationDecisionDetector from '@/components/ui/legislation-decision-detector';
import { LinkOptionsMenu } from '@/components/ui/link-options-menu';
import Loader from '@/elements/loader/Loader';
import useMediaQuery from '@/hooks/use-media-query';
import { useActiveHighlight } from '@/hooks/useActiveHighlight';
import {
  buildLeafHoverIds,
  findLeafHoverTarget,
  hasTextSelection,
  readMenuOffset,
} from '@/lib/utils/leafHoverTargets';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { HoverPreviewManager } from './HoverPreviewManager';
import { useHtmlDocumentFetch } from './useHtmlDocumentFetch';
import {
  addHighlightToSection,
  useSectionHighlight,
} from './useSectionHighlight';
import './eu-legislation.css';

interface HtmlViewerProps {
  /** Body HTML fetched during SSR; skips the client fetch when present. */
  initialSecHtml?: string | undefined;
  iri?: string | undefined;
  secHtmlStoragePath?: string | undefined;
  sudHtmlStoragePath?: string | undefined;
  documentId?: number | undefined;
}

/**
 * HtmlViewer - Main component for displaying HTML legal documents.
 *
 * Features:
 * - Data fetching (useHtmlDocumentFetch)
 * - URL hash highlighting (useSectionHighlight)
 * - Hover preview popups (HoverPreviewManager)
 * - Active highlights with click toggle (useActiveHighlight)
 * - Element options menu on hover (3-dot menu)
 * - Link options menu on click
 */
/**
 * Everything the viewer's container-level hover/click handlers need.
 *
 * Note this viewer's hover semantics differ from html-link-handler's on
 * purpose: it has no badge highlights, and it removes the hover outline
 * without exempting actively-highlighted sections. Only the leaf index and
 * geometry are shared (lib/utils/leafHoverTargets).
 */
interface ViewerHoverContext {
  container: HTMLElement;
  hoverOutlineClass: string;
  isHoveringMenuAreaRef: { current: boolean };
  currentHoverRef: { current: HTMLElement | null };
  hoveredElementIdRef: { current: string | null };
  activeElementIdRef: { current: string | null };
  menuHideTimeoutRef: { current: ReturnType<typeof setTimeout> | null };
  menuWrapperRef: { current: HTMLDivElement | null };
  findHoverTarget: (el: HTMLElement) => HTMLElement | null;
  startGracePeriod: () => void;
  toggleActiveHighlight: (id: string) => void;
}

/**
 * Synchronous hover — no RAF delay. All operations are cheap (string checks +
 * cached lookups). Reads (getBoundingClientRect) happen BEFORE writes
 * (classList changes) to avoid forced reflow on large documents.
 */
function viewerMouseOver(e: MouseEvent, ctx: ViewerHoverContext): void {
  // Skip links — they have their own hover preview (HoverPreviewManager)
  if ((e.target as HTMLElement).closest('a')) return;
  if (ctx.isHoveringMenuAreaRef.current) return;

  const target = ctx.findHoverTarget(e.target as HTMLElement);
  if (!target) return;
  if (target === ctx.currentHoverRef.current) return;

  // Cancel any pending hide timeout
  if (ctx.menuHideTimeoutRef.current) {
    clearTimeout(ctx.menuHideTimeoutRef.current);
    ctx.menuHideTimeoutRef.current = null;
  }

  // READ positions BEFORE any class changes — avoids forced reflow
  const wrapper = ctx.menuWrapperRef.current;
  const offset = wrapper ? readMenuOffset(ctx.container, target) : null;

  // WRITE phase — all DOM mutations batched together
  const previous = ctx.currentHoverRef.current;
  if (previous && previous !== target) {
    previous.classList.remove(ctx.hoverOutlineClass);
  }
  target.classList.add(ctx.hoverOutlineClass);
  ctx.currentHoverRef.current = target;

  if (wrapper && offset) {
    wrapper.style.transform = `translate(${offset.left}px, ${offset.top}px)`;
    wrapper.style.visibility = 'visible';
  }

  ctx.activeElementIdRef.current = target.id;
  ctx.hoveredElementIdRef.current = target.id;
}

/** Container-level `mouseout` for the viewer. */
function viewerMouseOut(e: MouseEvent, ctx: ViewerHoverContext): void {
  if (ctx.isHoveringMenuAreaRef.current) return;

  const target = ctx.findHoverTarget(e.target as HTMLElement);
  if (!target) return;

  // Skip if mouse moved to a child within the same section
  const related = e.relatedTarget as HTMLElement | null;
  if (related && target.contains(related)) return;

  if (target.id === ctx.hoveredElementIdRef.current) {
    ctx.startGracePeriod();
    return;
  }

  target.classList.remove(ctx.hoverOutlineClass);
  if (ctx.currentHoverRef.current === target) {
    ctx.currentHoverRef.current = null;
  }
}

/** Native click handler for section highlighting. */
function viewerClick(e: MouseEvent, ctx: ViewerHoverContext): void {
  const target = e.target as HTMLElement;

  // Don't trigger when user is selecting text (drag-select)
  if (hasTextSelection()) return;

  // Don't trigger on links - let them navigate normally
  if (target.closest('a')) return;

  // Don't trigger on menu buttons
  if (target.closest('[role="button"]') || target.closest('button')) return;

  // Reuse findHoverTarget for click (same exclusion + size logic)
  const elementWithId = ctx.findHoverTarget(target);
  if (!elementWithId) return;

  e.stopPropagation();
  ctx.toggleActiveHighlight(elementWithId.id);
}

/** Binds the viewer's three container-level handlers to one context. */
function createViewerHoverHandlers(ctx: ViewerHoverContext) {
  return {
    handleMouseOver: (e: MouseEvent) => viewerMouseOver(e, ctx),
    handleMouseOut: (e: MouseEvent) => viewerMouseOut(e, ctx),
    handleNativeClick: (e: MouseEvent) => viewerClick(e, ctx),
  };
}

/**
 * Pointer entered the floating menu area: cancel the pending hide, and re-add
 * the hover outline if the grace period had already removed it.
 */
function enterMenuArea(ctx: {
  menuHideTimeoutRef: { current: ReturnType<typeof setTimeout> | null };
  isHoveringMenuAreaRef: { current: boolean };
  activeElementIdRef: { current: string | null };
  hoverOutlineClass: string;
}): void {
  if (ctx.menuHideTimeoutRef.current) {
    clearTimeout(ctx.menuHideTimeoutRef.current);
    ctx.menuHideTimeoutRef.current = null;
  }
  ctx.isHoveringMenuAreaRef.current = true;

  const elId = ctx.activeElementIdRef.current;
  if (!elId) return;
  const element = document.getElementById(elId);
  if (element && !element.classList.contains(ctx.hoverOutlineClass)) {
    element.classList.add(ctx.hoverOutlineClass);
  }
}

/** Close buttons floating beside each active highlight. */
function HighlightCloseButtons({
  highlights,
  containerRef,
  removeActiveHighlight,
  label,
}: {
  highlights: { elementId: string }[];
  containerRef: { current: HTMLDivElement | null };
  removeActiveHighlight: (elementId: string) => void;
  label: string;
}) {
  const container = containerRef.current;
  if (!container) return null;
  const containerRect = container.getBoundingClientRect();

  return (
    <>
      {highlights.map((highlight) => {
        const element = document.getElementById(highlight.elementId);
        if (!element) return null;
        const rect = element.getBoundingClientRect();

        return (
          <button
            key={highlight.elementId}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              removeActiveHighlight(highlight.elementId);
            }}
            className='absolute z-50 bg-transparent rounded-full w-7 h-7 flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/10 transition-colors duration-150'
            style={{
              top: `${rect.top - containerRect.top - 4}px`,
              left: `${rect.right - containerRect.left + 2}px`,
            }}
            aria-label={label}
            title={label}
            type='button'
          >
            <span className='text-content-muted text-xl font-light leading-none hover:text-content-secondary'>
              ×
            </span>
          </button>
        );
      })}
    </>
  );
}

/**
 * The document content area: the loader, the rendered statute HTML, or the
 * empty state. Split out of HtmlViewer so each branch reads on its own.
 */
function DocumentBody({
  loading,
  processedSecHtml,
  secHtmlContent,
  sudHtmlContent,
  emptyLabel,
}: {
  loading: boolean;
  processedSecHtml: string | null | undefined;
  secHtmlContent: string | null | undefined;
  sudHtmlContent: string | null | undefined;
  emptyLabel: string;
}) {
  if (loading) {
    return (
      <div data-testid='document-loading'>
        <Loader height='h-screen' />
      </div>
    );
  }

  if (!secHtmlContent && !sudHtmlContent) {
    return (
      <div className='text-center text-content-muted py-10'>{emptyLabel}</div>
    );
  }

  return (
    <Fragment>
      {processedSecHtml && (
        <div
          className='html-content sec-html-section slov-lex-style'
          dangerouslySetInnerHTML={{ __html: processedSecHtml }}
        />
      )}
      {secHtmlContent && sudHtmlContent && (
        <div className='my-6 border-t border-line-default' />
      )}
      {sudHtmlContent && (
        <div
          className='html-content sud-html-section slov-lex-style'
          dangerouslySetInnerHTML={{ __html: sudHtmlContent }}
        />
      )}
    </Fragment>
  );
}

export default function HtmlViewer({
  initialSecHtml,
  iri,
  secHtmlStoragePath,
  sudHtmlStoragePath,
  documentId,
}: HtmlViewerProps) {
  const t = useTranslations('Common');
  const tCommon = useTranslations('Common');
  const params = useParams();
  const locale = (params?.['locale'] as string) || 'sk';

  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const isMobileQuery = useMediaQuery('(max-width: 767px)');
  const [contentReady, setContentReady] = useState(false);

  // Menu height for positioning
  const menuHeight = 150;

  // Hover system — refs only (no React state) for zero-lag hover feedback.
  // React state caused re-renders on every mouseover, triggering layout
  // thrashing via renderCloseButtons() and racing with DOM manipulation.
  const activeElementIdRef = useRef<string | null>(null);
  const hoveredElementIdRef = useRef<string | null>(null);
  const isHoveringMenuAreaRef = useRef(false);
  const currentHoverRef = useRef<HTMLElement | null>(null);
  const menuHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuWrapperRef = useRef<HTMLDivElement>(null);

  // Link menu states (React state OK — only changes on click, not hover)
  const [linkOptionsVisible, setLinkOptionsVisible] = useState(false);
  const [currentHref, setCurrentHref] = useState<string | null>(null);
  const [linkPosition, setLinkPosition] = useState({
    x: 0,
    y: 0,
    placeAbove: false,
  });

  useEffect(() => {
    setIsMobile(isMobileQuery);
  }, [isMobileQuery]);

  // Data fetching
  const { secHtmlContent, sudHtmlContent, loading, error } =
    useHtmlDocumentFetch({
      initialSecHtml,
      iri,
      secHtmlStoragePath,
      sudHtmlStoragePath,
    });

  // Section highlighting from URL hash
  const { targetSectionId } = useSectionHighlight();

  // Active highlight system (persistent yellow boxes)
  // CSS class names from module (with fallbacks for type safety)
  const activeHighlightClass = styles['activeHighlight'] ?? 'activeHighlight';
  const hoverOutlineClass = styles['hoverOutline'] ?? 'hoverOutline';

  const {
    highlights,
    addActiveHighlight,
    removeActiveHighlight,
    toggleActiveHighlight,
  } = useActiveHighlight(activeHighlightClass);

  const { highlightAndScrollToSection } =
    useActiveHighlightSection(addActiveHighlight);

  // Re-apply active highlights when DOM is mutated (dangerouslySetInnerHTML wipes classes)
  // Using MutationObserver to detect actual DOM changes instead of re-rendering on every state change
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new MutationObserver(() => {
      // Re-apply all active highlights after DOM mutation
      highlights.forEach((highlight) => {
        const element = document.getElementById(highlight.elementId);
        if (element && !element.classList.contains(activeHighlightClass)) {
          element.classList.remove(hoverOutlineClass);
          element.classList.add(activeHighlightClass);
        }
      });
    });

    observer.observe(containerRef.current, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [highlights, activeHighlightClass, hoverOutlineClass]);

  // Ensure highlights are applied when the highlights state changes
  useEffect(() => {
    highlights.forEach((highlight) => {
      const element = document.getElementById(highlight.elementId);
      if (element && !element.classList.contains(activeHighlightClass)) {
        element.classList.remove(hoverOutlineClass);
        element.classList.add(activeHighlightClass);
      }
    });
  }, [highlights, activeHighlightClass, hoverOutlineClass]);

  // Process HTML to add highlight class for URL hash
  const processedSecHtml = useMemo(() => {
    return addHighlightToSection(secHtmlContent, targetSectionId);
  }, [secHtmlContent, targetSectionId]);

  // Set content ready once HTML content is present
  useEffect(() => {
    // Set content ready after a small delay to ensure DOM is updated
    if (secHtmlContent || sudHtmlContent) {
      const timer = setTimeout(() => {
        setContentReady(true);
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [secHtmlContent, sudHtmlContent]);

  // Handle hash-based navigation
  useEffect(() => {
    if (!contentReady) return;

    const handleHashChange = () => {
      const rawHash = window.location.hash.substring(1);
      if (rawHash) {
        // Decode URI component to handle encoded characters
        const hash = decodeURIComponent(rawHash);

        highlightAndScrollToSection(hash);
      }
    };

    // Handle initial hash on load with retry logic
    if (window.location.hash) {
      const attemptScroll = (attempt: number) => {
        const rawHash = window.location.hash.substring(1);
        const hash = decodeURIComponent(rawHash);
        const element = document.getElementById(hash);

        if (element) {
          highlightAndScrollToSection(hash);
        } else if (attempt < 5) {
          // Retry up to 5 times with increasing delays
          setTimeout(() => attemptScroll(attempt + 1), 200 * (attempt + 1));
        } else {
          console.warn(
            '[HtmlViewer] Initial hash scroll - element not found after 5 attempts:',
            hash
          );
        }
      };

      // Start first attempt after initial delay
      setTimeout(() => attemptScroll(1), 300);
    }

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [contentReady, highlightAndScrollToSection]);

  // IDs and classes to exclude from highlighting - memoized to prevent recreating on each render
  const excludedElementIds = useMemo(
    () => [
      'predpis',
      'predpis.oznacenie',
      'predpis.typ',
      'predpis.datum',
      'predpis.nadpis',
      'predpis.text',
      'predpis.text2',
      'slov-lex-style',
      'iri',
    ],
    []
  );
  const excludedClassNames = useMemo(() => ['odsekOznacenie'], []);

  // Shared helper: hide both outline and menu button atomically.
  // Called from grace period timeouts and menu-area leave.
  const hideHoverUI = useCallback(() => {
    const el = hoveredElementIdRef.current
      ? document.getElementById(hoveredElementIdRef.current)
      : currentHoverRef.current;
    if (el) el.classList.remove(hoverOutlineClass);
    currentHoverRef.current = null;
    hoveredElementIdRef.current = null;
    activeElementIdRef.current = null;
    if (menuWrapperRef.current)
      menuWrapperRef.current.style.visibility = 'hidden';
  }, [hoverOutlineClass]);

  // Shared helper: start grace period timeout (100ms).
  // If neither the section nor the menu area is hovered when it fires, hide everything.
  const startGracePeriod = useCallback(() => {
    if (menuHideTimeoutRef.current) {
      clearTimeout(menuHideTimeoutRef.current);
    }
    menuHideTimeoutRef.current = setTimeout(() => {
      if (!isHoveringMenuAreaRef.current) {
        hideHoverUI();
      }
    }, 100);
  }, [hideHoverUI]);

  // Setup native event listeners for instant hover feedback and double-click
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !contentReady) return;

    // Leaf-level hover targets (odsek / písmeno / bod), built once per content
    // load. Shared with html-link-handler via lib/utils/leafHoverTargets.
    const leafIds = buildLeafHoverIds(
      container,
      excludedElementIds,
      excludedClassNames
    );
    const getLeafIds = () => leafIds;

    // Lazy cache: repeated hovers on the same DOM element are O(1).
    const hoverTargetCache = new WeakMap<HTMLElement, HTMLElement | null>();

    const findHoverTarget = (el: HTMLElement): HTMLElement | null =>
      findLeafHoverTarget(el, container, getLeafIds, hoverTargetCache);

    // Synchronous hover — no RAF delay. All operations are cheap (string
    // checks + cached lookups). Reads (getBoundingClientRect) happen BEFORE
    // writes (classList changes) to avoid forced reflow on large documents.

    const { handleMouseOver, handleMouseOut, handleNativeClick } =
      createViewerHoverHandlers({
        container,
        hoverOutlineClass,
        isHoveringMenuAreaRef,
        currentHoverRef,
        hoveredElementIdRef,
        activeElementIdRef,
        menuHideTimeoutRef,
        menuWrapperRef,
        findHoverTarget,
        startGracePeriod,
        toggleActiveHighlight,
      });

    // Use native event listeners for better performance
    container.addEventListener('mouseover', handleMouseOver, { passive: true });
    container.addEventListener('mouseout', handleMouseOut, { passive: true });
    container.addEventListener('click', handleNativeClick);

    return () => {
      container.removeEventListener('mouseover', handleMouseOver);
      container.removeEventListener('mouseout', handleMouseOut);
      container.removeEventListener('click', handleNativeClick);
      if (menuHideTimeoutRef.current) {
        clearTimeout(menuHideTimeoutRef.current);
        menuHideTimeoutRef.current = null;
      }
    };
  }, [
    contentReady,
    toggleActiveHighlight,
    excludedClassNames,
    excludedElementIds,
    hoverOutlineClass,
    startGracePeriod,
  ]);

  // Link click handler - shows link options menu
  const handleLinkClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const link = target.closest('a');

    if (link) {
      e.preventDefault();
      e.stopPropagation();

      const href = link.getAttribute('href') || '';
      const rect = link.getBoundingClientRect();

      const containerRect = containerRef.current?.getBoundingClientRect();

      if (!containerRect) return;

      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const placeAbove = spaceBelow < menuHeight;

      setCurrentHref(href);
      setLinkPosition({
        x: Math.max(0, rect.left - containerRect.left),
        y: placeAbove
          ? rect.top - containerRect.top
          : rect.bottom - containerRect.top,
        placeAbove,
      });

      setLinkOptionsVisible(true);
    }
  };

  // Handle link option selection
  const handleOptionSelected = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setLinkOptionsVisible(false);
  };

  // Getter for active element ID — read from ref at click time (avoids re-renders)
  const getActiveElementId = useCallback(
    () => activeElementIdRef.current ?? '',
    []
  );

  const hasContent = secHtmlContent || sudHtmlContent;

  return (
    <div className='html-viewer relative'>
      {error && (
        <div
          data-testid='document-error'
          className='border-b border-status-error/20 bg-status-error/10 p-4'
        >
          <p className='text-sm text-status-error'>{error}</p>
        </div>
      )}

      <div
        ref={containerRef}
        data-testid='document-content'
        className={`html-content-container legislation-content ${styles['contentContainer']}`}
        onClick={handleLinkClick}
      >
        <DocumentBody
          loading={loading}
          processedSecHtml={processedSecHtml}
          secHtmlContent={secHtmlContent}
          sudHtmlContent={sudHtmlContent}
          emptyLabel={
            error ? t('failedToLoadContent') : t('noContentAvailable')
          }
        />
      </div>

      {/* Element options menu — always mounted, visibility toggled via DOM
          for instant appearance in the same frame as the hover outline */}
      <div
        ref={menuWrapperRef}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
          willChange: 'transform',
        }}
      >
        <div
          style={{
            pointerEvents: 'auto',
            width: '16px',
            height: '24px',
          }}
          onMouseEnter={() => {
            enterMenuArea({
              menuHideTimeoutRef,
              isHoveringMenuAreaRef,
              activeElementIdRef,
              hoverOutlineClass,
            });
          }}
          onMouseLeave={() => {
            isHoveringMenuAreaRef.current = false;
            // Start grace period — if mouse doesn't re-enter the section, hide everything
            startGracePeriod();
          }}
        >
          <ElementOptionsMenu
            getElementId={getActiveElementId}
            activeElementPosition={{ x: 0, y: 0, width: 0, height: 0 }}
            iri={iri}
            documentId={documentId}
          />
        </div>
      </div>

      {/* Link options menu */}
      <LinkOptionsMenu
        href={currentHref || ''}
        position={linkPosition}
        visible={linkOptionsVisible}
        onClose={() => setLinkOptionsVisible(false)}
        onOptionSelected={handleOptionSelected}
        iri={iri}
      />

      {/* Close buttons for active highlights */}
      <HighlightCloseButtons
        highlights={Array.from(highlights.values())}
        containerRef={containerRef}
        removeActiveHighlight={removeActiveHighlight}
        label={tCommon('removeHighlight')}
      />

      {/* Hover preview manager - event delegation for legislation links */}
      <HoverPreviewManager
        containerRef={containerRef}
        locale={locale}
        enabled={!!hasContent && !loading}
      />

      {/* Legislation decision detector - inline section buttons (rendered after content loads) */}
      {!isMobile && iri && contentReady && (
        <LegislationDecisionDetector
          iri={iri.startsWith('/') ? iri.substring(1) : iri}
          key={`detector-${iri}`}
        />
      )}
    </div>
  );
}
